import * as XLSX from 'xlsx'
import { formatDate } from '@/lib/date'
import type { IssueReportSummary, OperatorReportRow, OverviewReport, SalesReport, SlaRow, ThroughputReportRow } from '@/types'

export type ReportTab = 'overview' | 'operators' | 'throughput' | 'issues' | 'sales'

export interface ReportData {
  overview?: OverviewReport
  operators?: OperatorReportRow[]
  throughput?: ThroughputReportRow[]
  sla?: SlaRow[]
  issues?: IssueReportSummary
  sales?: SalesReport
}

type Cell = string | number | null | undefined

const TITLES: Record<ReportTab, string> = {
  overview: 'MPG Production overview',
  operators: 'MPG Operator report',
  throughput: 'MPG Throughput report',
  issues: 'MPG Issue report',
  sales: 'MPG Sales report',
}

/**
 * One workbook per tab, one sheet per table on it. Every sheet keeps the
 * same title row + blank row + header recipe the original export used, and
 * money stays numeric so Excel can sum it.
 */
export function buildReportWorkbook(tab: ReportTab, range: { from: string; to: string }, data: ReportData): XLSX.WorkBook | null {
  const book = XLSX.utils.book_new()
  const title = TITLES[tab]
  const add = (name: string, header: string[], rows: Cell[][]) => {
    const sheet = XLSX.utils.aoa_to_sheet([[title, `${range.from} to ${range.to}`], [], header, ...rows])
    sheet['!cols'] = header.map(() => ({ wch: 18 }))
    XLSX.utils.book_append_sheet(book, sheet, name)
  }

  switch (tab) {
    case 'overview': {
      const d = data.overview
      if (!d) return null
      add('SUMMARY', ['Metric', 'Value'], [
        ['Jobs completed', d.kpis.jobs_completed],
        ['Pieces completed', d.kpis.pieces_completed],
        ['Stages completed', d.kpis.stages_completed],
        ['Issues flagged', d.kpis.issues_flagged],
        ['Issues open now', d.kpis.issues_open_now],
        ['Avg turnaround (days)', d.kpis.avg_turnaround_days ?? '-'],
        ['On time (%)', d.kpis.on_time_pct ?? '-'],
      ])
      add('DAILY', ['Date', 'Pieces', 'Stages', 'Issues'], d.daily.map((r) => [formatDate(r.date), r.pieces, r.stages, r.issues]))
      add('DEPARTMENTS', ['Department', 'Jobs completed', 'Pieces'], d.departments.map((r) => [r.department, r.jobs_completed, r.pieces]))
      break
    }
    case 'operators': {
      if (!data.operators) return null
      add('OPERATORS',
        ['Operator', 'Department', 'Started', 'Completed', 'Assisted', 'Issues', 'Jobs touched', 'Pieces credited',
          'Stages measured', 'Avg minutes per stage', 'Fastest minutes', 'Fastest job',
          'Slowest minutes', 'Slowest job', 'Days worked', 'Avg start', 'Avg end'],
        data.operators.map((r) => [
          r.operator, r.department ?? '-', r.stages_started, r.stages_completed, r.stages_assisted ?? 0, r.issues_flagged,
          r.jobs_touched, r.pieces_completed,
          // Raw minutes rather than "2h 10m": a spreadsheet should be able to
          // sort and average these.
          r.stages_measured, r.avg_stage_minutes ?? '-',
          r.fastest_stage?.minutes ?? '-', r.fastest_stage?.job_code ?? '-',
          r.slowest_stage?.minutes ?? '-', r.slowest_stage?.job_code ?? '-',
          r.days_worked, r.avg_start_clock ?? '-', r.avg_end_clock ?? '-',
        ]))
      break
    }
    case 'throughput': {
      if (!data.throughput) return null
      add('THROUGHPUT', ['Department', 'Jobs completed', 'Pieces'], data.throughput.map((r) => [r.department, r.jobs_completed, r.pieces]))
      if (data.sla) {
        const verdict = (v: boolean | null) => (v === null ? '-' : v ? 'Yes' : 'No')
        add('SLA',
          ['Department', 'SLA days', 'Jobs measured', 'Avg actual days', 'On track',
            'Urgent SLA days', 'Urgent jobs', 'Urgent avg days', 'Urgent on track'],
          data.sla.map((r) => [
            r.department, r.sla_days, r.jobs_measured, r.avg_actual_days ?? '-', verdict(r.on_track),
            r.urgent_sla_days, r.urgent_jobs_measured, r.avg_actual_urgent_days ?? '-', verdict(r.on_track_urgent),
          ]))
      }
      break
    }
    case 'issues': {
      const d = data.issues
      if (!d) return null
      add('SUMMARY', ['Flagged', 'Open', 'Resolved', 'Cleared'], [[d.totals.flagged, d.totals.open, d.totals.resolved, d.totals.cleared]])
      add('BY REASON', ['Reason', 'Section', 'Issues'], d.by_reason.map((r) => [r.reason, r.department ?? '-', r.count]))
      add('BY SECTION', ['Department', 'Issues'], d.by_department.map((r) => [r.department, r.count]))
      add('BY OPERATOR', ['Operator', 'Department', 'Issues'], d.by_operator.map((r) => [r.operator, r.department ?? '-', r.count]))
      add('DAILY', ['Date', 'Issues'], d.daily.map((r) => [formatDate(r.date), r.count]))
      break
    }
    case 'sales': {
      const d = data.sales
      if (!d) return null
      add('SUMMARY', ['Orders', 'Revenue (RM)', 'Pieces', 'Avg order value (RM)', 'Collected (RM)', 'Outstanding (RM)', 'Refunds due (RM)'],
        [[d.totals.orders, d.totals.revenue, d.totals.pieces, d.totals.avg_order_value, d.totals.collected ?? 0, d.totals.outstanding ?? 0, d.totals.refunds_due ?? 0]])
      add(d.granularity === 'month' ? 'MONTHLY' : 'DAILY', ['Period', 'Orders', 'Revenue (RM)', 'Pieces'],
        d.series.map((r) => [d.granularity === 'month' ? r.label : formatDate(r.date), r.orders, r.revenue, r.pieces]))
      add('TOP CUSTOMERS', ['Customer', 'Company', 'Orders', 'Revenue (RM)'], d.top_customers.map((r) => [r.customer, r.company_name ?? '-', r.orders, r.revenue]))
      add('TOP PRODUCTS', ['Product', 'Qty', 'Revenue (RM)'], d.top_products.map((r) => [r.product, r.qty, r.revenue]))
      add('BY STATUS', ['Status', 'Orders'], d.by_status.map((r) => [r.status.replace(/_/g, ' '), r.count]))
      break
    }
  }

  return book.SheetNames.length ? book : null
}
